/** BTS Configure Tasks → Task table schedule helpers. */

export const TASK_DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

/** Hourly slots from ConfigureTasks.xaml (12:00 AM … 11:00 PM). */
export const TASK_TIME_SLOTS = [
    "12:00 AM",
    "1:00 AM",
    "2:00 AM",
    "3:00 AM",
    "4:00 AM",
    "5:00 AM",
    "6:00 AM",
    "7:00 AM",
    "8:00 AM",
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
    "7:00 PM",
    "8:00 PM",
    "9:00 PM",
    "10:00 PM",
    "11:00 PM",
];

export const TASK_ACTIONS = [
    { key: "printReport", label: "Print", tooltip: "Auto Scan Print Reports" },
    { key: "emailReport", label: "Email", tooltip: "Auto Scan Email Reports" },
    { key: "printGmsReport", label: "GMS Print", tooltip: "Print GMS" },
    { key: "emailGmsReport", label: "GMS Email", tooltip: "Email GMS" },
];

const DEFAULT_TASK_RAN_ON = "2012-01-01T04:00:00";
const BTS_TASK_REFERENCE_DATE = "2012-01-01";

function timeLabelTo24Hour(timeLabel) {
    const match = String(timeLabel ?? "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hours: 4, minutes: 0 };
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return { hours, minutes };
}

function format24HourAsTaskLabel(hours24, minutes) {
    const ampm = hours24 >= 12 ? "PM" : "AM";
    let hours12 = hours24 % 12;
    if (hours12 === 0) hours12 = 12;
    return `${hours12}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

function parseTimeLabel(dateValue) {
    if (dateValue == null || dateValue === "") return null;
    const text = String(dateValue).trim();

    // Prefer literal clock time from ISO/SQL strings (BTS Task.RunAt is time-of-day only).
    const isoMatch = text.split("Z")[0].split("+")[0].match(/T(\d{1,2}):(\d{2})/);
    if (isoMatch) {
        const hours24 = parseInt(isoMatch[1], 10);
        const minutes = parseInt(isoMatch[2], 10);
        const label = format24HourAsTaskLabel(hours24, minutes);
        return TASK_TIME_SLOTS.includes(label) ? label : null;
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    const useUtc = text.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(text);
    const hours24 = useUtc ? date.getUTCHours() : date.getHours();
    const minutes = useUtc ? date.getUTCMinutes() : date.getMinutes();
    const label = format24HourAsTaskLabel(hours24, minutes);
    return TASK_TIME_SLOTS.includes(label) ? label : null;
}

/** Wall-clock RunAt on BTS reference date — no UTC shift (matches ConfigureTasks TimeOfDay). */
function runAtFromTimeLabel(timeLabel) {
    const { hours, minutes } = timeLabelTo24Hour(timeLabel);
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");
    return `${BTS_TASK_REFERENCE_DATE}T${hh}:${mm}:00`;
}

function emptyTaskSlot() {
    return {
        printReport: false,
        emailReport: false,
        printGmsReport: false,
        emailGmsReport: false,
    };
}

export function createEmptyTaskSchedule() {
    const schedule = {};
    for (const day of TASK_DAYS) {
        schedule[day] = {};
        for (const time of TASK_TIME_SLOTS) {
            schedule[day][time] = emptyTaskSlot();
        }
    }
    return schedule;
}

function taskBool(task, camel, pascal) {
    return !!(task?.[camel] ?? task?.[pascal]);
}

export function parseTaskScheduleFromTasks(tasks) {
    const schedule = createEmptyTaskSchedule();
    for (const task of tasks ?? []) {
        const day = task?.day ?? task?.Day;
        const time = parseTimeLabel(task?.runAt ?? task?.RunAt);
        if (!day || !time || !schedule[day]?.[time]) continue;
        schedule[day][time] = {
            printReport: taskBool(task, "printReport", "PrintReport"),
            emailReport: taskBool(task, "emailReport", "EmailReport"),
            printGmsReport: taskBool(task, "printGmsReport", "PrintGmsReport"),
            emailGmsReport: taskBool(task, "emailGmsReport", "EmailGmsReport"),
        };
    }
    return schedule;
}

/** Empty / missing SpecificBin means all bins (BTS Configure Tasks). */
export function parseSelectedBinsFromTasks(tasks) {
    if (!tasks?.length) return [];
    const raw = tasks[0]?.specificBin ?? tasks[0]?.SpecificBin ?? "";
    const text = String(raw).trim();
    if (!text) return [];
    return text.split(",").map((part) => part.trim()).filter(Boolean);
}

export function buildSpecificBinValue(selectedBinNames, allBinNames) {
    const names = (selectedBinNames ?? []).map((n) => String(n).trim()).filter(Boolean);
    if (!names.length || names.length >= (allBinNames?.length ?? 0)) {
        return "";
    }
    return names.map((name) => `${name},`).join("");
}

export function selectedBinsForUi(selectedBinNames, allBinNames) {
    const all = (allBinNames ?? []).map((n) => String(n).trim()).filter(Boolean);
    const picked = (selectedBinNames ?? []).map((n) => String(n).trim()).filter(Boolean);
    if (!picked.length || picked.length >= all.length) return all;
    return picked;
}

/** Latest RanOn across existing Task rows — applied to every slot on save. */
function latestRanOnFromExistingTasks(existingTasks) {
    let latestMs = null;
    let latestValue = null;
    for (const t of existingTasks ?? []) {
        const raw = t?.ranOn ?? t?.RanOn;
        if (raw == null || raw === "") continue;
        const ms = Date.parse(raw);
        if (Number.isNaN(ms)) continue;
        if (latestMs == null || ms > latestMs) {
            latestMs = ms;
            latestValue = raw;
        }
    }
    return latestValue ?? DEFAULT_TASK_RAN_ON;
}

export function buildTasksFromSchedule(schedule, selectedBinNames, allBinNames, existingTasks) {
    const specificBin = buildSpecificBinValue(selectedBinNames, allBinNames);
    const ranOn = latestRanOnFromExistingTasks(existingTasks);
    const tasks = [];

    for (const day of TASK_DAYS) {
        for (const time of TASK_TIME_SLOTS) {
            const slot = schedule?.[day]?.[time] ?? emptyTaskSlot();
            if (!slot.printReport && !slot.emailReport && !slot.printGmsReport && !slot.emailGmsReport) {
                continue;
            }
            tasks.push({
                day,
                runAt: runAtFromTimeLabel(time),
                // Same latest RanOn on every row so Site Setup save does not reset stamps.
                ranOn,
                specificBin,
                specificCable: "",
                printReport: !!slot.printReport,
                emailReport: !!slot.emailReport,
                printGmsReport: !!slot.printGmsReport,
                emailGmsReport: !!slot.emailGmsReport,
                restarted: false,
            });
        }
    }
    return tasks;
}

export function prepareTasksForSave(setup) {
    const allBinNames = (setup?.bins ?? []).map((b) => b.name).filter(Boolean);
    return buildTasksFromSchedule(
        setup?.taskSchedule ?? createEmptyTaskSchedule(),
        setup?.taskSelectedBins ?? [],
        allBinNames,
        setup?.tasks ?? []
    );
}

export function hydrateTaskSetupFromPayload(setup) {
    const tasks = setup?.tasks ?? setup?.Tasks ?? [];
    return {
        ...setup,
        tasks,
        taskSchedule: parseTaskScheduleFromTasks(tasks),
        taskSelectedBins: parseSelectedBinsFromTasks(tasks),
    };
}
